import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, User as UserIcon, Sparkles, LogOut, Share2 } from 'lucide-react';
import type { User } from '../../types/index.js';
import type { NavTab } from './NavLinks.js';
import { LazyAvatar } from '../LazyAvatar.js';

interface UserDropdownProps {
  user: User;
  isOpen: boolean;
  onToggleOpen: () => void;
  onClose: () => void;
  onSelectTab: (tab: NavTab) => void;
  onOpenProfile?: () => void;
  onOpenShowcase?: (type: 'penulis' | 'penerbit', identifier: string) => void;
  onLogout: () => void;
}

export const UserDropdown: React.FC<UserDropdownProps> = ({
  user,
  isOpen,
  onToggleOpen,
  onClose,
  onSelectTab,
  onOpenProfile,
  onOpenShowcase,
  onLogout,
}) => {
  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return (
          <span className="px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 font-bold text-[10px] uppercase border border-rose-200">
            Admin
          </span>
        );
      case 'penerbit':
        return (
          <span className="px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 font-bold text-[10px] uppercase border border-purple-200">
            Penerbit
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 font-bold text-[10px] uppercase border border-indigo-200">
            Penulis
          </span>
        );
    }
  };

  return (
    <div className="relative">
      <button
        id="btn-user-profile-menu"
        type="button"
        onClick={onToggleOpen}
        className="flex items-center gap-2 p-1.5 sm:px-3 sm:py-1.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition cursor-pointer min-h-[44px] min-w-[44px]"
      >
        <LazyAvatar
          src={user.profileImageUrl}
          alt={user.name}
          size="xs"
          priority
          className="w-7 h-7 rounded-lg shrink-0 shadow-2xs"
        />
        <div className="text-left hidden sm:block">
          <span className="block text-xs font-bold text-slate-900 leading-tight max-w-[110px] truncate">
            {user.name}
          </span>
          <span className="block text-[10px] text-slate-400 capitalize">
            {user.role}
          </span>
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 hidden sm:block" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-slate-100 p-2 z-50"
          >
            <div className="p-3 border-b border-slate-100 flex items-center gap-3">
              <LazyAvatar
                src={user.profileImageUrl}
                alt={user.name}
                size="sm"
                className="w-9 h-9 rounded-xl shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-slate-900 truncate">{user.name}</p>
                <p className="text-[11px] text-slate-500 truncate">{user.email}</p>
              </div>
            </div>
            <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
              {getRoleBadge(user.role)}
              {user.publisherCode && (
                <span className="text-[10px] font-mono text-slate-400">
                  Kode: {user.publisherCode}
                </span>
              )}
            </div>

            {/* Profile & Account Settings Action */}
            <div className="p-1 border-b border-slate-100">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  const targetIdentifier = user.organization || user.name;
                  const showcaseType = user.role === 'penerbit' ? 'penerbit' : 'penulis';
                  if (onOpenShowcase) {
                    onOpenShowcase(showcaseType, targetIdentifier);
                  } else {
                    window.history.pushState({}, '', `/${showcaseType}/${encodeURIComponent(targetIdentifier)}`);
                    window.dispatchEvent(new Event('popstate'));
                  }
                }}
                className="w-full text-left px-2.5 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:text-indigo-600 hover:bg-indigo-50/70 transition flex items-center justify-between group cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <Share2 className="w-3.5 h-3.5 text-indigo-600" />
                  <span>Halaman Publik UIBN Saya</span>
                </div>
                <span className="text-[10px] text-indigo-500 font-medium group-hover:translate-x-0.5 transition">
                  Lihat &rarr;
                </span>
              </button>

              <button
                id="btn-open-profile-menu"
                type="button"
                onClick={() => {
                  onClose();
                  if (onOpenProfile) {
                    onOpenProfile();
                  } else {
                    onSelectTab('profile');
                  }
                }}
                className="w-full text-left px-2.5 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:text-indigo-600 hover:bg-indigo-50/70 transition flex items-center justify-between group cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <UserIcon className="w-3.5 h-3.5 text-indigo-600" />
                  <span>Profil & Pengaturan Akun</span>
                </div>
                <span className="text-[10px] text-indigo-500 font-medium group-hover:translate-x-0.5 transition">
                  Buka &rarr;
                </span>
              </button>

              {user.role === 'admin' && (
                <button
                  id="btn-admin-blog"
                  type="button"
                  onClick={() => {
                    onClose();
                    onSelectTab('admin-blog');
                  }}
                  className="w-full text-left px-2.5 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:text-indigo-600 hover:bg-indigo-50/70 transition flex items-center justify-between group cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                    <span>Kelola Artikel Blog</span>
                  </div>
                  <span className="text-[10px] text-amber-600 font-medium group-hover:translate-x-0.5 transition">
                    Kelola &rarr;
                  </span>
                </button>
              )}
            </div>

            <button
              id="btn-logout"
              type="button"
              onClick={() => {
                onClose();
                onLogout();
              }}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-xl transition flex items-center gap-2 mt-1 cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Keluar Akun</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
